// src/components/IconReplacement/IconReplacementHover.tsx
// ✨ Visual feedback when hovering over existing icon for replacement
// Supports both: PLACE_ICON mode (from sidebar) and DRAG_ITEMS mode (existing icons)
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useScene } from '@/hooks/useScene';
import { getItemAtTile, getTilePosition } from '@/utils';
import { useIcon } from '@/hooks/useIcon';
import { useModelStore } from '@/stores/modelStore';

/**
 * IconReplacementHover - Shows visual highlight when hovering to replace
 *
 * Displays a pulsing blue border around an existing icon when the user
 * is dragging a new icon over it, indicating that dropping will replace.
 *
 * Works in two modes:
 * 1. PLACE_ICON - When selecting icon from sidebar and hovering over existing icon
 * 2. DRAG_ITEMS - When dragging an existing icon over another icon
 */
export const IconReplacementHover: React.FC = () => {
  const mode = useUiStateStore((state) => state.mode);
  const mouse = useUiStateStore((state) => state.mouse);
  const scene = useScene();
  const modelItems = useModelStore((state) => state.items);

  // Determine if we're in a replacement-capable mode
  const isPlaceIconMode = mode.type === 'PLACE_ICON' && mode.id !== null;
  const isDragItemsMode = mode.type === 'DRAG_ITEMS' && mode.items?.length === 1 && mode.items[0].type === 'ITEM';

  const isReplacementMode = isPlaceIconMode || isDragItemsMode;

  // Get the icon ID being dragged/placed
  const draggedIconId = useMemo(() => {
    if (isPlaceIconMode && mode.type === 'PLACE_ICON') {
      return mode.id;
    }
    if (isDragItemsMode && mode.type === 'DRAG_ITEMS') {
      const draggedItemId = mode.items[0].id;
      const modelItem = modelItems.find(m => m.id === draggedItemId);
      return modelItem?.icon || null;
    }
    return null;
  }, [isPlaceIconMode, isDragItemsMode, mode, modelItems]);

  // Get the dragged item ID (for DRAG_ITEMS mode to exclude self)
  const draggedItemId = useMemo(() => {
    if (isDragItemsMode && mode.type === 'DRAG_ITEMS') {
      return mode.items[0].id;
    }
    return null;
  }, [isDragItemsMode, mode]);

  // Get item at current mouse position
  const hoveredItem = useMemo(() => {
    if (!isReplacementMode) return null;

    const itemAtTile = getItemAtTile({
      tile: mouse.position.tile,
      scene
    });

    // Only show hover for ITEM type, and not the same item being dragged
    if (itemAtTile?.type === 'ITEM') {
      // In DRAG_ITEMS mode, don't show hover on the item being dragged
      if (draggedItemId && itemAtTile.id === draggedItemId) {
        return null;
      }
      return itemAtTile;
    }

    return null;
  }, [isReplacementMode, mouse.position.tile, scene, draggedItemId]);

  // Calculate position for highlight
  const highlightPosition = useMemo(() => {
    if (!hoveredItem) return null;

    // ✅ Use scene.items directly (useScene already provides this)
    const viewItem = scene.items?.find((item) => item.id === hoveredItem.id);
    if (!viewItem) return null;

    const tilePos = getTilePosition({ tile: viewItem.tile, origin: 'BOTTOM' });
    return tilePos;
  }, [hoveredItem, scene.items]);

  // ✨ Get the replacement icon preview
  const { iconComponent: replacementIconComponent } = useIcon(
    draggedIconId || '',
    draggedIconId ? `replacement-preview-${draggedIconId}` : ''
  );

  // Don't render if not hovering over an item
  if (!hoveredItem || !highlightPosition) {
    return null;
  }

  return (
    <>
      {/* ✨ Icon Preview Container - positioned at the icon location */}
      <Box
        sx={{
          position: 'absolute',
          opacity: 0.6,
          pointerEvents: 'none',
          zIndex: 9998 // Just below the border/badge
        }}
        style={{
          left: highlightPosition.x,
          top: highlightPosition.y
        }}
      >
        {replacementIconComponent}
      </Box>

      {/* Pulsing Border Container */}
      <div
        style={{
          position: 'absolute',
          left: highlightPosition.x,
          top: highlightPosition.y,
          width: '72px',
          height: '72px',
          pointerEvents: 'none',
          zIndex: 9999
        }}
      >
        {/* Pulsing Border */}
        <div
          style={{
            position: 'absolute',
            inset: '-4px',
            border: '3px solid #3b82f6',
            borderRadius: '8px',
            animation: 'pulse-border 1.5s ease-in-out infinite',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
          }}
        />

        {/* Replacement Indicator Badge */}
        <div
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            animation: 'bounce 0.5s ease-in-out infinite alternate'
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
        </div>

        {/* Tooltip */}
        <div
          style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 10000
          }}
        >
          {isDragItemsMode ? 'Drop to swap icons' : 'Drop to replace icon'}
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: '5px solid rgba(0, 0, 0, 0.9)'
            }}
          />
        </div>

        {/* Inline animations */}
        <style>
          {`
            @keyframes pulse-border {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.6;
                transform: scale(1.05);
              }
            }

            @keyframes bounce {
              from {
                transform: translateY(0);
              }
              to {
                transform: translateY(-4px);
              }
            }
          `}
        </style>
      </div>
    </>
  );
};
